<template>
  <div v-if="!readOnly && !field.readOnly" :ref="inputRef">
    <div class="input-group mb-3">
      <label>{{ field.label }} {{ field.required ? '*' : '' }}</label>
    </div>
    <v-card tile color="grey lighten-5">
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="8" class="reg-photo-thumb card__media__content pa-1">
            <div>
              <v-img v-if="localImageUrl"
                     :src="localImageUrl"
                     contain
                     aspect-ratio="1"
                     height="160" />
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <v-row class="ml-2">
              <v-col v-if="!localImageUrl && photo.id" cols="6" class="pa-1">
                <v-btn outlined block @click="downloadByPhotoPath(photo.path)">
                  <v-icon>file_download</v-icon>
                  <span>{{ $t('download') }}</span>
                </v-btn>
              </v-col>
              <v-col v-if="(localImageUrl || photo.id) && $isOnline && !readOnly && !field.readOnly" cols="6" class="pa-1">
                <v-btn outlined block color="red" @click="doDeleteDocument(photo)">
                  <v-icon>close</v-icon>
                  <span>{{ $t('delete') }}</span>
                </v-btn>
              </v-col>
              <v-col v-if="fileName" cols="12" class="pa-1">
                <span>{{ fileName }}</span>
              </v-col>
              <v-col v-if="size" cols="12" class="pa-1">
                <span>{{ size | formatBytes }}</span>
              </v-col>
              <v-col v-if="!localImageUrl && !photo.id && !readOnly && !field.readOnly" cols="12">
                <reg-upload-button :multiple="false"
                                   :label="$t(field.forcePhotoCapture ? 'take_photo' : 'select_image')"
                                   :force-photo-capture="field.forcePhotoCapture"
                                   image-upload
                                   block
                                   outline
                                   color="primary"
                                   icon="mdi-camera"
                                   @files-selected="onFilesSelected" />
              </v-col>
              <v-col cols="12">
                <p v-if="field.hint" class="text-body-2 grey--text text--darken-3 mb-0">
                  {{ field.hint }}
                </p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
  <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
    <label>{{ field.label }}</label>
    <v-img v-if="localImageUrl"
           :src="localImageUrl"
           contain
           aspect-ratio="1"
           height="160" />
    <p v-else>
      {{ $t('no_image_registered') }}
    </p>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, getCurrentInstance, toRef } from 'vue'
import { useDynamicField } from '../composables/useDynamicField'
import { useDownload } from '../composables/useDownload'
import UploadButton from '~sendData/UploadButton.vue'
import { isEmpty } from 'lodash'

export default defineComponent({
  name: 'DynamicPhotoField',

  components: {
    'reg-upload-button': UploadButton
  },

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },

  emits: ['input', 'change', 'file-selected', 'upload-complete'],

  setup(props, { emit, expose }) {
    const instance = getCurrentInstance()
    const $store = instance?.proxy?.$store
    const $t = instance?.proxy?.$t || ((key) => key)
    const uploadService = instance?.proxy?.$upload
    const confirmService = instance?.proxy?.$confirm

    const {
      fieldValue,
      keyValue,
      setFieldValue,
      inputRef,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: {},
      emit
    })

    const { downloadByPhotoPath } = useDownload()

    // Local state
    const localImageUrl = ref('')
    const localFileSelected = ref(false)
    const filesUploader = ref({})

    // Computed properties
    const photo = computed(() => fieldValue.value || {})

    const size = computed(() => {
      if (photo.value.size) {
        return photo.value.size
      }
      return 0
    })

    const fileName = computed(() => {
      if (photo.value.displayName) {
        return photo.value.displayName
      }
      return ''
    })

    // Methods
    const startUpload = () => {
      if (!isEmpty(filesUploader.value)) {
        filesUploader.value.startUpload()
      }
    }

    const cancelUpload = () => {
      if (!isEmpty(filesUploader.value)) {
        filesUploader.value.cancelAll()
      }
    }

    const createFileUploader = () => {
      if (!props.field.uploadBasePath) {
        throw new Error('uploadBasePath is required')
      }

      if (isEmpty(filesUploader.value)) {
        filesUploader.value = uploadService?.getUploader({
          multiple: true,
          private: !props.field.publicFile,
          basePath: props.field.uploadBasePath,
          sizeLimit: props.field.uploadSizeLimit || 1024 * 1024 * 1024,
          autoStart: false,
          context: props.field.uploadContext || {}
        })
        filesUploader.value.$on('all-upload-complete', onAllFilesUploadComplete)
      }
    }

    const onFilesSelected = async (e) => {
      const file = e.files[0]

      if (file) {
        createFileUploader()

        const fr = new FileReader()
        fr.readAsDataURL(file)
        fr.addEventListener('load', () => {
          localImageUrl.value = fr.result
        })

        await filesUploader.value.addFiles([file], true).then((filesInfos) => {
          const fileInfo = filesInfos[0]
          setFieldValue({
            url: fileInfo.fullPath,
            width: fileInfo.width,
            height: fileInfo.height,
            kilobytes: fileInfo.fileSize / 1024
          })
        })
      } else {
        localImageUrl.value = ''
      }
    }

    const onAllFilesUploadComplete = async (e) => {
      const { succeeded } = e
      const fileInfos = []
      for (const fileId of succeeded) {
        const fileInfo = await filesUploader.value.getFileInfo(fileId)
        fileInfos.push(fileInfo)
      }

      filesUploader.value = null
      emit('upload-complete', fileInfos)
    }

    const updateFieldValue = () => {
      if (isEmpty(fieldValue.value)) {
        localImageUrl.value = ''
      } else {
        localImageUrl.value = fieldValue.value.url
      }
    }

    const doDeleteDocument = () => {
      const message = 'Deseja realmente excluir esta imagem?'
      const url = fieldValue.value.url
      if (!url) {
        if (!isEmpty(filesUploader.value)) {
          filesUploader.value.cancelAll()
        }
        setFieldValue({})
      } else if (url) {
        confirmService({
          title: $t('warning'),
          message,
          confirmText: $t('yes'),
          cancelText: $t('no')
        }).then((confirmed) => {
          if (!confirmed) return
          setFieldValue({})
        })
      }
    }

    const deletePhoto = ({ id }) => {
      $store.dispatch('deletePhoto', { id })
    }

    // Watchers
    watch(fieldValue, (newVal, oldVal) => {
      if (oldVal && !newVal) {
        localImageUrl.value = ''
      }
      updateFieldValue()
    })

    watch(keyValue, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        localImageUrl.value = ''
      }
    })

    // Lifecycle
    onMounted(() => {
      updateFieldValue()
    })

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      fieldValue,
      localImageUrl,
      localFileSelected,
      filesUploader,
      photo,
      size,
      fileName,
      startUpload,
      cancelUpload,
      onFilesSelected,
      doDeleteDocument,
      deletePhoto,
      downloadByPhotoPath,
      inputRef
    }
  }
})
</script>

<style lang="scss">
.reg-photo-thumb {
  border: solid #ccc 1px;
  height: 170px;
  text-align: center;
  vertical-align: middle;
  div {
    width: 100%;
  }
  canvas,
  img {
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
